import { useAsync } from 'react-use';

import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { listEntitiesOwnedByUserGroups } from './ownedEntities';
import { useGetUserGroups } from './useGetUserGroups';

const KUBERNETES_CLUSTER_TYPE = 'kubernetes-cluster';

const CLUSTER_FIELDS = [
  'metadata.title',
  'metadata.name',
  'metadata.namespace',
  'metadata.annotations',
  'kind',
  'spec.type',
  'spec.owner',
];

/**
 * Fetches the `kubernetes-cluster` resource entities from the catalog.
 *
 * With `userOwnedCluster` the query is narrowed to the clusters owned by one of
 * the caller's own supervisory-org groups; an unresolved group list yields an
 * empty result rather than every cluster.
 */
export const useGetKubernetesClusters = (userOwnedCluster: boolean = false) => {
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    value: userGroups,
    loading: userGroupsLoading,
    error: userGroupsError,
  } = useGetUserGroups();

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    if (userOwnedCluster) {
      return listEntitiesOwnedByUserGroups({
        catalogApi,
        errorApi,
        userGroups,
        userGroupsLoading,
        userGroupsError,
        filter: { kind: 'Resource', 'spec.type': KUBERNETES_CLUSTER_TYPE },
        fields: CLUSTER_FIELDS,
      });
    }

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Resource',
        'spec.type': KUBERNETES_CLUSTER_TYPE,
      },
      fields: CLUSTER_FIELDS,
    });
    return items;
  }, [userOwnedCluster, userGroupsLoading, userGroups, userGroupsError]);

  return { value: entities, loading, error };
};
